import React from 'react';
import { Bell, Users, Calendar, Heart, MessageCircle, UserPlus, Clock } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { useNavigate, useLocation } from 'react-router-dom';

/**
 * NotificationItem Component
 *
 * Displays a single notification with icon, message, and timestamp
 * Navigates to the notification's webLink when clicked
 */
const NotificationItem = ({
  notification,
  onClick,
  onMarkAsRead,
}) => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  // Get icon based on notification type
  const getNotificationIcon = (type) => {
    switch (type) {
      case 'group_invitation':
      case 'group_member_added':
        return <Users className="w-5 h-5" />;
      case 'event_invitation':
      case 'event_reminder':
      case 'event_updated':
        return <Calendar className="w-5 h-5" />;
      case 'group_post_like':
        return <Heart className="w-5 h-5" />;
      case 'group_post_comment':
        return <MessageCircle className="w-5 h-5" />;
      case 'friend_request':
        return <UserPlus className="w-5 h-5" />;
      default:
        return <Bell className="w-5 h-5" />;
    }
  };

  // Format timestamp
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';

    const now = new Date();
    const notificationDate = new Date(timestamp);
    const diffMs = now - notificationDate;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return notificationDate.toLocaleDateString();
  };

  // Check if notification is unread (supports both isRead boolean and status string)
  const isUnread = notification.isRead === false || notification.status === 'unread';

  const handleClick = (e) => {
    e.stopPropagation(); // Prevent event bubbling


    // Mark as read
    if (isUnread && onMarkAsRead) {
      onMarkAsRead(notification.id);
    }

    // Navigate to webLink if available (check both camelCase and snake_case)
    const link = notification.webLink || notification.web_link;

    if (link) {
      // Use window.location.href for full page navigation
      window.location.href = link;
      return; // Exit early to ensure navigation happens
    } else {
      console.warn('No webLink found in notification');
    }

    // Call onClick callback if provided (only if no link navigation)
    if (onClick) {
      onClick(notification);
    }
  };

  return (
    <div className="mb-2">
      <div
        onClick={handleClick}
        className={`flex items-start gap-x-3 p-4 rounded-lg cursor-pointer transition-colors ${theme.backgroundSecondary} hover:${theme.cardHover}`}
      >
        {/* Icon */}
        <div
          className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
            !isUnread
              ? `${theme.backgroundSecondary}`
              : 'bg-blue-500 text-white'
          }`}
        >
          {getNotificationIcon(notification.type)}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {notification.title && (
            <h4 className={`font-semibold ${theme.text} text-sm mb-1`}>
              {notification.title}
            </h4>
          )}
          <p className={`text-sm ${theme.textSecondary} line-clamp-2`}>
            {notification.message}
          </p>
          <div className="flex items-center gap-x-2 mt-2">
            <Clock className={`w-3 h-3 ${theme.textMuted}`} />
            <span className={`text-xs ${theme.textMuted}`}>
              {formatTimestamp(notification.createdAt || notification.metadata?.timestamp)}
            </span>
          </div>
        </div>

        {/* Unread indicator */}
        {isUnread && (
          <div className="flex-shrink-0">
            <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationItem;
